import React, { memo, useCallback, useEffect, useState } from 'react';
import styles from 'assets/css/OverviewComponent.module.css';
import CardLink from 'Components/Card/CardLink';
import { IconAngleRight, IconTargetArrows } from '@ace/react-icons';
import { allActions } from '@ace/react-utilities';
import { useDispatch } from 'react-redux';
import { StatusSlashWarn, StatusTriangleWarn } from 'Components/StatusMessage';
import { STATUS_STATE } from 'utils/statusUtils';
import ScheduleCoursePartials from 'Components/Card/Partials/Partials.ScheduleCourse';
import { getUTCDateFromServerDate } from 'utils/dateUtils';
import useSMARTGoalOverlay from 'context/SMARTGoalOverlayContext';
import SMARTGoalPartials from 'Components/Card/Partials/Partials.SMARTGoals';
import { defaultModuleState, formatCardModuleState, OverviewModuleState, getOverviewCardModuleDisplayProps } from 'Components/Card/helper/overviewModuleHelpers';
import CoursePartials from 'Components/Card/Partials/Partials.Course';

function OverviewCardModule(props) {
    const { ModuleTitle, Ordinal, CompletionDate, VoucherExpiration, ModuleLink, SMARTGoalStartDate, SMARTGoalEndDate, SMARTGoalTimeLeft } = props;
    const displayProps = getOverviewCardModuleDisplayProps(props);

    const dispatch = useDispatch();

    const setActiveTab = useCallback(() => {
        dispatch(allActions.dashboardTabActions.setActiveTab(props.DashboardTabKey));
    }, [dispatch, props.DashboardTabKey]);

    return (
        <div>
            <h3 className={styles.header} onClick={() => setActiveTab()}><span className={styles.headerNumber}>{Ordinal}</span>{ModuleTitle}</h3>
            {displayProps.showComplete && <ScheduleCoursePartials.CompleteContent completionDate={getUTCDateFromServerDate(CompletionDate)} />}
            {displayProps.showVoucher && <ScheduleCoursePartials.VoucherContent voucherExpirationDate={getUTCDateFromServerDate(VoucherExpiration)} />}
            {displayProps.showNotPassed && <StatusSlashWarn>Did not pass</StatusSlashWarn>}
            {displayProps.showAudit && <StatusTriangleWarn>Audit Needed</StatusTriangleWarn>}
            {displayProps.showSmartGoalInfo && <SMARTGoalPartials.SMARTGoalDisplay {...{ SMARTGoalStartDate, SMARTGoalEndDate, SMARTGoalTimeLeft }} />}
            {displayProps.showSmartGoalToolLink && <SMARTGoalToolLink />}
            {displayProps.showLink && <OverviewCardModuleLink {...ModuleLink} />}
            {displayProps.showLocked && <CoursePartials.LockedBtn />}
        </div>
    )
}

function withModuleWrapper(Component) {
    function WithModuleWrapper(props) {
        const [moduleDisplayState, setModuleDisplayState] = useState(defaultModuleState);
        const { ModuleState: moduleState } = props;

        //store progress state and show in wrapper
        useEffect(() => {
            switch (moduleState) {
                case OverviewModuleState.IN_PROGRESS:
                    setModuleDisplayState(formatCardModuleState(STATUS_STATE.INFO, STATUS_STATE.INFO, 'In Progress'));
                    break;
                case OverviewModuleState.BEHIND_SCHEDULE:
                    setModuleDisplayState(formatCardModuleState(STATUS_STATE.ALERT, STATUS_STATE.ALERT, 'Behind Schedule'));
                    break;
                case OverviewModuleState.VOUCHER_EXPIRING:
                    setModuleDisplayState(formatCardModuleState(STATUS_STATE.WARN, STATUS_STATE.WARN, 'Voucher Expires Soon'));
                    break;
                case OverviewModuleState.NOT_PASSED:
                case OverviewModuleState.AUDIT_NEEDED:
                    setModuleDisplayState(formatCardModuleState(STATUS_STATE.WARN, STATUS_STATE.NONE, null));
                    break;
                default:
                    setModuleDisplayState(defaultModuleState);
            }
        }, [moduleState]);

        return (
            <div className={`${styles.overviewSubModuleContainer} ${moduleDisplayState.moduleWrapperClass}`}>
                {moduleDisplayState.hasBanner && <span className={`${styles.overviewSubModuleBanner} ${moduleDisplayState.moduleBannerClass}`}>{moduleDisplayState.moduleBanner}</span>}
                <Component {...props} />
            </div>
        )
    }

    return WithModuleWrapper;
}

function OverviewCardModuleLink(props) {
    return (
    <CardLink
        linkText={props.CardLinkText}
        linkIcon={<IconAngleRight />}
        linkUrl={props.CardLinkUri}
        target="_blank"
        className={`${styles.courseBtn}`} />)
}

const SMARTGoalToolLink = memo(() => {
    const { toggleSMARTGoalsOverlay } = useSMARTGoalOverlay();

    return (
        <span onClick={() => toggleSMARTGoalsOverlay()} className={styles.moduleSMARTGoalToolLink}>
            <IconTargetArrows />
            <span className={styles.moduleSMARTGoalDatesSet}>Set your SMART goals now.</span>
        </span>
    );
});

export default withModuleWrapper(OverviewCardModule);
